const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue | QueryValue[]> | URLSearchParams;

interface HttpOptions {
  params?: QueryParams;
  headers?: HeadersInit;
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  if (params instanceof URLSearchParams) {
    const value = params.toString();
    return value ? `?${value}` : '';
  }

  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, raw]) => {
    const values = Array.isArray(raw) ? raw : [raw];
    values.forEach((value) => {
      if (value !== null && value !== undefined && value !== '') {
        query.append(key, String(value));
      }
    });
  });
  const value = query.toString();
  return value ? `?${value}` : '';
}

function scopedPath(tenantId: string, path: string): string {
  if (!tenantId) {
    throw new Error('clinic_required');
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/panel-cash/tenants/${encodeURIComponent(tenantId)}${normalizedPath}`;
}

async function requestJson<T>(
  token: string | null,
  tenantId: string,
  method: string,
  path: string,
  body?: unknown,
  options?: HttpOptions
): Promise<T> {
  if (!token) {
    throw new Error('unauthorized');
  }

  const hasInlineQuery = path.includes('?');
  const query = hasInlineQuery ? '' : buildQuery(options?.params);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${scopedPath(tenantId, path)}${query}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options?.headers ?? {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Error('network_error');
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new Error(payload?.message ?? payload?.error ?? 'request_failed');
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return await response.json() as T;
}

export function createPanelCashHttpClient(token: string | null, tenantId: string) {
  return {
    get: <T>(path: string, options?: HttpOptions) => requestJson<T>(token, tenantId, 'GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: HttpOptions) => requestJson<T>(token, tenantId, 'POST', path, body, options),
    put: <T>(path: string, body?: unknown, options?: HttpOptions) => requestJson<T>(token, tenantId, 'PUT', path, body, options),
    delete: <T>(path: string, options?: HttpOptions) => requestJson<T>(token, tenantId, 'DELETE', path, undefined, options),
  };
}
